import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import quotesApi from '../../../api/quotesApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import QuoteDetailDialog from '../../../sections/admin/quotes/QuoteDetailDialog';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });
const currencyUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const statusColors = {
  borrador: 'default',
  enviada: 'info',
  convertida: 'success',
};

export default function QuotesListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [statuses, setStatuses] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const { data } = await quotesApi.getQuoteStatuses();
        setStatuses(data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los estados.'), { variant: 'error' });
      }
    };

    loadStatuses();
  }, [enqueueSnackbar]);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await quotesApi.getQuotes({
        search: search || undefined,
        status: statusFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de cotizaciones.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleDetailChanged = () => {
    fetchQuotes();
  };

  const handleConfirmDelete = async () => {
    if (!quoteToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await quotesApi.deleteQuote(quoteToDelete.id);
      enqueueSnackbar('Cotización eliminada correctamente.', { variant: 'success' });
      setQuoteToDelete(null);
      fetchQuotes();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la cotización.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'customer_name', headerName: 'Cliente', flex: 1, minWidth: 180 },
      { field: 'customer_email', headerName: 'Correo', flex: 1, minWidth: 200 },
      {
        field: 'subtotal_pen',
        headerName: 'Total (S/)',
        width: 130,
        valueFormatter: (value) => currencyPen.format(value || 0),
      },
      {
        field: 'subtotal_usd',
        headerName: 'Total (US$)',
        width: 130,
        valueFormatter: (value) => currencyUsd.format(value || 0),
      },
      {
        field: 'status',
        headerName: 'Estado',
        width: 130,
        renderCell: (params) => (
          <Chip
            label={params.value?.name || '—'}
            color={statusColors[params.value?.name] || 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Fecha',
        width: 130,
        valueFormatter: (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="view"
            icon={<VisibilityIcon />}
            label="Ver detalle"
            onClick={() => setSelectedQuote(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setQuoteToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4">Cotizaciones</Typography>
        <Typography variant="body2" color="text.secondary">
          Cotizaciones recibidas desde el cotizador público de la tienda.
        </Typography>
      </div>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nombre o correo del cliente"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowClick={(params) => setSelectedQuote(params.row)}
        />
      </Card>

      <QuoteDetailDialog
        open={Boolean(selectedQuote)}
        onClose={() => setSelectedQuote(null)}
        quote={selectedQuote}
        statuses={statuses}
        onChanged={handleDetailChanged}
      />

      <Dialog open={Boolean(quoteToDelete)} onClose={() => setQuoteToDelete(null)}>
        <DialogTitle>Eliminar cotización</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar la cotización de <strong>{quoteToDelete?.customer_name}</strong>? Esta acción
            se puede revertir solo desde la base de datos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
